import React from 'react';
import { BaseTool, IToolManifest, IToolContext, IToolComponentProps } from '../../core/tools/interfaces/ITool';
import { ICommand } from '../../core/commands/ICommand';
import { ChangeNodeStyleCommand } from '../../core/commands/ChangeNodeStyleCommand';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../components/ui/command';
import { Check, ChevronDown } from 'lucide-react';
import { TOOL_APPLICABILITY } from '../../core/tools/config/toolApplicability';
import { useTranslation } from '../../hooks/useTranslation';

const fontFamilies = [
  { value: 'Noto Sans', label: 'Noto Sans', category: 'sans-serif' },
  { value: 'Inter', label: 'Inter', category: 'sans-serif' },
  { value: 'Arial', label: 'Arial', category: 'sans-serif' },
  { value: 'Helvetica', label: 'Helvetica', category: 'sans-serif' },
  { value: 'Roboto', label: 'Roboto', category: 'sans-serif' },
  { value: 'Open Sans', label: 'Open Sans', category: 'sans-serif' },
  { value: 'Georgia', label: 'Georgia', category: 'serif' },
  { value: 'Times New Roman', label: 'Times New Roman', category: 'serif' },
  { value: 'Garamond', label: 'Garamond', category: 'serif' },
  { value: 'Merriweather', label: 'Merriweather', category: 'serif' },
  { value: 'Courier New', label: 'Courier New', category: 'monospace' },
  { value: 'Monaco', label: 'Monaco', category: 'monospace' },
  { value: 'Consolas', label: 'Consolas', category: 'monospace' },
  { value: 'JetBrains Mono', label: 'JetBrains Mono', category: 'monospace' },
  { value: 'Comic Sans MS', label: 'Comic Sans MS', category: 'display' },
  { value: 'Impact', label: 'Impact', category: 'display' },
  { value: 'Brush Script MT', label: 'Brush Script', category: 'handwriting' },
];

const FontPickerToolComponent: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const { t } = useTranslation('diagram');
  const [open, setOpen] = React.useState(false);
  const initialFont = context.selectionStyles?.fontFamily || 'Arial';
  const [selectedFont, setSelectedFont] = React.useState<string>(initialFont);
  const [isMixed, setIsMixed] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState('');

  const selectedCount = context.selection.size;
  const isDisabled = selectedCount === 0;

  React.useEffect(() => {
    if (isDisabled) {
      setIsMixed(false);
      setSelectedFont('Arial');
      return;
    }
    const mixedRaw = Boolean(context.selectionStyles?.isMixed?.fontFamily);
    const resolved = context.selectionStyles?.fontFamily;
    // When no explicit font is present (all undefined), treat as the default Arial — not Mixed.
    const mixed = mixedRaw && typeof resolved === 'string' && resolved.trim().length > 0 ? true : false;
    setIsMixed(mixed);
    setSelectedFont(resolved || 'Arial');
  }, [isDisabled, context.selectionStyles?.fontFamily, context.selectionStyles?.isMixed]);

  const handleFontChange = (fontFamily: string) => {
    setIsMixed(false);
    setSelectedFont(fontFamily);
    onExecute({ fontFamily });
    setOpen(false);
  };

  const filteredFonts = fontFamilies.filter(font =>
    font.label.toLowerCase().includes(searchValue.toLowerCase()) ||
    font.category.toLowerCase().includes(searchValue.toLowerCase())
  );

  // Group fonts by category
  const fontsByCategory = filteredFonts.reduce((acc, font) => {
    if (!acc[font.category]) {
      acc[font.category] = [];
    }
    acc[font.category].push(font);
    return acc;
  }, {} as Record<string, typeof fontFamilies>);

  const selectedFontOption = fontFamilies.find(f => f.value === selectedFont);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="tid.tools.fontFamily.trigger"
          disabled={isDisabled}
          className="flex items-center gap-0.5 px-2 h-8 rounded hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={t('tools.fontFamily')}
        >
          <span 
            className="text-sm text-foreground truncate max-w-[80px]" 
            style={{ fontFamily: isMixed ? undefined : selectedFont }}
          >
            {isMixed
              ? t('tools.fontFamilyMixed', { defaultValue: 'Mixed' })
              : (selectedFontOption?.label || selectedFont || fontFamilies[0]?.label || 'Noto Sans')}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        <Command>
          <CommandInput
            data-testid="tid.tools.fontFamily.search"
            placeholder={t('tools.searchFontsPlaceholder')}
            value={searchValue}
            onValueChange={setSearchValue}
            className="h-8 text-xs"
          />
          <CommandList>
            <CommandEmpty>{t('tools.noFontFound')}</CommandEmpty>
            {Object.entries(fontsByCategory).map(([category, fonts]) => (
              <CommandGroup
                key={category}
                heading={t(`tools.fontCategory.${category}`, {
                  defaultValue: category.charAt(0).toUpperCase() + category.slice(1).replace(/-/g, ' '),
                })}
              >
                {fonts.map((font) => (
                  <CommandItem
                    key={font.value}
                    data-testid={`tid.tools.fontFamily.option.${font.value.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}
                    value={font.value}
                    onSelect={() => handleFontChange(font.value)}
                    className="text-xs"
                  >
                    <span style={{ fontFamily: font.value }}>{font.label}</span>
                    {selectedFont === font.value && (
                      <Check className="ml-auto h-3 w-3" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export class FontPickerTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'font-picker',
    name: 'tools.fontFamily',
    icon: 'font',
    category: 'style',
    description: 'Change font family of selected text',
    version: '1.0.0',
    applicability: TOOL_APPLICABILITY.TEXT_SHAPES,
    
    requiresSelection: true,
    minSelection: 1,
    
    ui: {
      toolbar: true,
      contextMenu: false,
      propertyPanel: true,
      sidebar: false,
      floatingPanel: true,
      statusBar: false
    },
    
    capabilities: {
      modifiesShapes: true,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: true,
      supportsBatchOperations: true
    }
  };

  canExecute(context: IToolContext): boolean {
    return super.canExecute(context) && context.selection.size > 0;
  }

  execute(params?: { fontFamily: string }): ICommand | null {
    if (!this.context || this.context.selection.size === 0 || !params?.fontFamily) {
      return null;
    }

    const selectedIds = Array.from(this.context.selection);
    return new ChangeNodeStyleCommand(selectedIds, 'fontFamily', params.fontFamily);
  }

  getToolbarComponent() {
    return FontPickerToolComponent;
  }

  getFloatingPanelComponent() {
    return FontPickerToolComponent;
  }
}
